// Mediapipe component for pose estimation
//
// This component is a wrapper around the mediapipe pose estimation
// model. It takes a photo as input and returns the pose estimation
// results.

import {
  FilesetResolver,
  PoseLandmarker,
  DrawingUtils,
  NormalizedLandmark,
  Landmark,
} from "@mediapipe/tasks-vision";
import { useEffect, useRef, useState } from "react";
import SvgHeadWithArrowRight from "./SvgHead";
import {
  connections,
  getFacing,
  isMoving,
  isOkLandmark,
  keys,
  toIndex,
  isInside,
  visibleLandmarksSide,
  visibleLandmarksSideLeft,
  visibleLandmarksBack,
  visibleLandmarksFront,
} from "./PoseChecker";
import { getPoseHintV2, isConnectionErrorV2, isPoseOKV2 } from "./PoseCheckerV2";
import { classifyPoseErrors, PoseError } from "./PoseErrorClassifier";
import { drawGuidanceOverlay } from "../utils/guidanceOverlay";

// COCO-17 index → compact array index (mirrors PoseDebugPage mapping)
const COCO_TO_COMPACT = [0, 2, 1, 4, 3, 6, 5, 8, 7, 10, 9, 12, 11, 14, 13, 16, 15] as const;

interface MediaPipeProps {
  setOK: (ok: boolean) => void;
  setLoaded: (loaded: boolean) => void;
  setErrors?: (errors: PoseError[]) => void;
  setMoving?: (moving: boolean) => void;
  setV2Hint?: (hint: string | null) => void;
  /** Called every processed frame with COCO-17 keypoints in video pixel space and pose OK flag */
  onKeypoints?: (kpPx: [number, number][], videoWidth: number, videoHeight: number, poseOk: boolean) => void;
  /** When true, draws the silhouette guidance overlay on the canvas each frame */
  showGuidance?: boolean;
  id: string;
  mirrored?: boolean;
  /** When true, mirrors the side silhouette (used for left-view which reuses right pose logic) */
  flipped?: boolean;
  /** When true, excludes face landmarks from validation (used for back-view where face points away) */
  back?: boolean;
  shouldRunPose: (id: string, log?: boolean) => boolean;
  pose: "front" | "right";
}

const R_SH = toIndex(keys.rightShoulder);
const R_EL = toIndex(keys.rightElbow);
const R_WR = toIndex(keys.rightWrist);
const L_SH = toIndex(keys.leftShoulder);
const L_EL = toIndex(keys.leftElbow);
const L_WR = toIndex(keys.leftWrist);
const R_HIP = toIndex(keys.rightHip);
const R_KNE = toIndex(keys.rightKnee);
const R_ANK = toIndex(keys.rightAnkle);
const L_HIP = toIndex(keys.leftHip);
const L_KNE = toIndex(keys.leftKnee);
const L_ANK = toIndex(keys.leftAnkle);
//let RUN: boolean = false;
let MIRROR: boolean = false;
//
function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
export const MediaPipe: React.FC<MediaPipeProps> = ({
  setOK,
  //run,
  mirrored,
  pose,
  id,
  shouldRunPose,
  setLoaded,
  setErrors,
  setMoving,
  setV2Hint,
  onKeypoints,
  showGuidance,
  flipped,
  back,
}) => {
  const [webcamRunning, setWebcamRunning] = useState(true);
  // Use a ref so the render loop closure always reads the latest pose value.
  const poseRef = useRef(pose);
  // Ref so the render loop closure can read the latest showGuidance value.
  const showGuidanceRef = useRef(showGuidance);
  useEffect(() => { showGuidanceRef.current = showGuidance; }, [showGuidance]);
  const flippedRef = useRef(flipped ?? false);
  useEffect(() => { flippedRef.current = flipped ?? false; }, [flipped]);
  const backRef = useRef(back ?? false);
  useEffect(() => { backRef.current = back ?? false; }, [back]);

  useEffect(() => {
    loadPose(id);
  }, [id]);
  useEffect(() => {
    poseRef.current = pose;
  }, [pose]);
  useEffect(() => {
    if (mirrored !== MIRROR) {
    }
    MIRROR = mirrored === true;
  }, [mirrored]);

  const shouldRun = (id: string) => {
    return shouldRunPose(id);
  };

  async function loadPose(id: string) {
    /*   if (run === false) {
      return;
    } */
    console.log("MediaPipe STARTING!!!");
    try {

    const runningMode = "VIDEO";
    const vision = await FilesetResolver.forVisionTasks(
      "/shared/wasm"
    );

    let poseLandmarker: PoseLandmarker;
    try {
      poseLandmarker = await PoseLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: `/shared/models/pose_landmarker_lite.task`,
          delegate: "GPU",
        },
        runningMode: runningMode,
        minPosePresenceConfidence: 0.5,
        numPoses: 1,
        minTrackingConfidence: 0.1,
      });
      console.log("MediaPipe: GPU delegate loaded");
    } catch (gpuErr) {
      console.warn("MediaPipe: GPU delegate failed, falling back to CPU", gpuErr);
      poseLandmarker = await PoseLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: `/shared/models/pose_landmarker_lite.task`,
          delegate: "CPU",
        },
        runningMode: runningMode,
        minPosePresenceConfidence: 0.5,
        numPoses: 1,
        minTrackingConfidence: 0.1,
      });
      console.log("MediaPipe: CPU delegate loaded");
    }

    // Model is ready — signal the UI immediately (don't wait for first frame)
    setLoaded(true);

    let lastVideoTime = -1;
    let i = 0;
    let framesOK = 0;
    let badFrames = 0;

    function renderLoop(
      id: string,
      poseInWorld?: Landmark[][],
      poseInDraw?: Landmark[][]
    ): void {
      let poseLocalWorld: Landmark[][] = poseInWorld || [];
      let poseLocalDraw: Landmark[][] = poseInDraw || [];
      if (!shouldRunPose(id)) {
        // Idle-poll: model is loaded, waiting for pose_guidance state
        setTimeout(() => renderLoop(id, poseInWorld, poseInDraw), 200);
        return;
      }
      const video = document.querySelector("video") as HTMLVideoElement;

      if (!video || !video.videoWidth || !video.videoHeight) {
        setTimeout(() => {
          console.log(shouldRunPose(id, true));
          console.log("settimeout");
          renderLoop(id);
        }, 1000);
        return;
      }

      const canvasEl = document.getElementById("canvas") as HTMLCanvasElement;
      if (!canvasEl) {
        console.log("No canvas element");
       /*  sleep(200).then(() => {
          requestAnimationFrame(() => renderLoop(id));
        }); */
        //throw new Error("No canvas element");

        return;
      }

      const canvasCtx = canvasEl.getContext("2d") as CanvasRenderingContext2D;
      if (!canvasCtx) {
        console.log("No canvas context");
        requestAnimationFrame(() => renderLoop(id));
        throw new Error("No canvas context");
      }
      if (!shouldRun(id)) {
        // Idle-poll: waiting for pose_guidance state
        canvasCtx.clearRect(0, 0, canvasEl.width, canvasEl.height);
        setTimeout(() => renderLoop(id, poseInWorld, poseInDraw), 200);
        return;
      }
      canvasEl.width = video.videoWidth;
      canvasEl.height = video.videoHeight;

      // Match canvas CSS size to the actual rendered area of the video.
      // The video uses object-fit:cover (uniform scale, centred, overflow cropped).
      // If we just set width/height:100% the canvas stretches non-uniformly and
      // landmarks no longer align with the person.
      const containerEl = canvasEl.parentElement;
      const containerW = containerEl?.offsetWidth  ?? video.videoWidth;
      const containerH = containerEl?.offsetHeight ?? video.videoHeight;
      const coverScale = Math.max(containerW / video.videoWidth, containerH / video.videoHeight);
      const displayW   = video.videoWidth  * coverScale;
      const displayH   = video.videoHeight * coverScale;
      canvasEl.style.position = "absolute";
      canvasEl.style.top      = `${(containerH - displayH) / 2}px`;
      canvasEl.style.left     = `${(containerW - displayW) / 2}px`;
      canvasEl.style.width    = `${displayW}px`;
      canvasEl.style.height   = `${displayH}px`;
      canvasEl.style.marginLeft = "";
      canvasEl.style.marginTop  = "";
      const drawingUtils = new DrawingUtils(canvasCtx);

      const toShow = Object.values(keys);
      const startTimeMs = performance.now(); // video.currentTime * 1000;
      if (video.currentTime !== lastVideoTime) {
        poseLandmarker.detectForVideo(video, startTimeMs, (result) => {
          if (i % 250 === 0 && result.landmarks.length > 0) {
            console.log(i);
            console.log(
              "MediaPipe result " + id,
              result.landmarks[0][0]
            );
          }
          const marginH = 0.05;
          const marginW = poseRef.current === "front" ? 0.05 : 0.25;
          // center box corners based on the margins in normalized coordinates
          lastVideoTime = video.currentTime;

          canvasCtx.clearRect(0, 0, canvasEl.width, canvasEl.height);

          let frameOK = true;
          if (result.landmarks.length === 0) {
            // No pose detected — draw ghost guidance outline if enabled
            if (showGuidanceRef.current) {
              const empty: [number, number][] = Array(17).fill([-1, -1]);
              drawGuidanceOverlay(canvasCtx, empty, poseRef.current, video.videoWidth, video.videoHeight, false, 1, 1, 1, flippedRef.current);
            }
            framesOK = 0;
            badFrames = 999;
            setOK(false);
            setV2Hint?.(null);
          }
          if (result.landmarks.length > 0) {
            i++;
            const drawLandmarks = result.landmarks[0]; // Used for drawing the landmarks.
            const worldLandmarks = result.worldLandmarks[0]; // used for calculating the angles
            let poseFinalWorld: Landmark[] = [];
            let poseFinalDraw: Landmark[] = [];
            const videoSize = {
              width: video.videoWidth,
              height: video.videoHeight,
            };
            let average = calculateAveragePose(
              drawLandmarks,
              toShow,
              poseLocalDraw
            );
            poseFinalDraw = average.result;
            poseLocalDraw = average.updatedList;
            // TODO rename averageWorld to averageCalc
            // Normalize the landmarks to the video size
            let averageWorld = calculateAveragePose(
              drawLandmarks,
              toShow,
              poseLocalWorld,
              videoSize
            );
            //averageWorld = calculateAveragePose(worldLandmarks, toShow, poseLocalWorld);
            poseFinalWorld = averageWorld.result;
            poseLocalWorld = averageWorld.updatedList;

            // Classify pose errors and surface them for voice/UI
            if (poseFinalWorld.length === 17) {
              setV2Hint?.(getPoseHintV2(poseFinalWorld, poseRef.current, flippedRef.current ? "left" : "right", backRef.current));
              const cocoKpPx = COCO_TO_COMPACT.map(
                (ci) => [poseFinalWorld[ci].x, poseFinalWorld[ci].y] as [number, number]
              );
              if (setErrors) {
                const errors = classifyPoseErrors({
                  cocoKpPx,
                  view: poseRef.current,
                  imageWidth: video.videoWidth,
                  imageHeight: video.videoHeight,
                });
                setErrors(errors);
              }
              onKeypoints?.(cocoKpPx, video.videoWidth, video.videoHeight, isPoseOKV2(poseFinalWorld, poseRef.current, flippedRef.current ? "left" : "right"));
              if (showGuidanceRef.current) {
                drawGuidanceOverlay(canvasCtx, cocoKpPx, poseRef.current, video.videoWidth, video.videoHeight, isPoseOKV2(poseFinalWorld, poseRef.current, flippedRef.current ? "left" : "right"), 1, 1, 1, flippedRef.current);
              }
              if (setMoving && poseLocalDraw.length > 1) {
                setMoving(isMoving(poseLocalDraw as NormalizedLandmark[][], poseFinalDraw as NormalizedLandmark[]));
              }
            }

            // Determine which landmarks should be visible for this view.
            // Used both for skeleton drawing and the isInside boundary check.
            const visible =
              poseRef.current === "right"
                ? (flippedRef.current ? visibleLandmarksSideLeft : visibleLandmarksSide)
                : backRef.current ? visibleLandmarksBack : visibleLandmarksFront;

            // Check that visible-set landmarks are within the frame.
            // We intentionally exclude face landmarks for back view because they
            // point away from the camera and MediaPipe places them outside the
            // frame, causing frameOK to be permanently false.
            const visiblePoseFinalDraw = poseFinalDraw.filter(
              (_, i) => visible.includes(Object.values(keys)[i])
            );
            const isInsideBox = isInside(visiblePoseFinalDraw, marginW, marginH);
            if (!isInsideBox) setV2Hint?.("Step back from the camera");
            frameOK = frameOK && isInsideBox;

            if (poseRef.current === "right") {
              const face = getFacing(poseFinalWorld);
              if (flippedRef.current) {
                // Left view: person faces left in world space
                const facingLeft = face === "left";
                if (face && !facingLeft) {
                  frameOK = false;
                  setV2Hint?.("Turn to face left");
                }
              } else {
                const facingRight = MIRROR ? face === "right" : face === "left";
                if (face && !facingRight) {
                  frameOK = false;
                  setV2Hint?.("Turn to face right");
                }
              }
            }
            // Back view: verify person is actually facing away (not sideways)
            if (backRef.current) {
              const face = getFacing(poseFinalWorld);
              const faceVisibilityIndices = [
                toIndex(keys.nose),
                toIndex(keys.rightEye),
                toIndex(keys.leftEye),
              ];
              const visibleFacePoints = faceVisibilityIndices
                .map((idx) => poseFinalWorld[idx])
                .filter((lm) => lm && lm.visibility === 1);
              const frontFaceVisible = visibleFacePoints.length >= 2;

              if (face === "right" || face === "left" || frontFaceVisible) {
                frameOK = false;
                setV2Hint?.("Turn your back to the camera");
              }
            }
            const visibleByIndex = Object.values(keys).map((key) =>
              visible.includes(key)
            );
            const v2ConnError = connections.map((_, idx) =>
              isConnectionErrorV2(poseFinalWorld, idx, poseRef.current, flippedRef.current ? "left" : "right")
            );
            const connectionsToDraw = connections
              .map((conn, sourceIndex) => ({ ...conn, sourceIndex }))
              .filter((conn) => visibleByIndex[conn.start] && visibleByIndex[conn.end]);

            const limbErrorByLandmark = new Set<number>();
            if (v2ConnError[0] || v2ConnError[1]) {
              limbErrorByLandmark.add(R_SH);
              limbErrorByLandmark.add(R_EL);
              limbErrorByLandmark.add(R_WR);
            }
            if (v2ConnError[2] || v2ConnError[3]) {
              limbErrorByLandmark.add(L_SH);
              limbErrorByLandmark.add(L_EL);
              limbErrorByLandmark.add(L_WR);
            }
            if (v2ConnError[4] || v2ConnError[5]) {
              limbErrorByLandmark.add(R_HIP);
              limbErrorByLandmark.add(R_KNE);
              limbErrorByLandmark.add(R_ANK);
            }
            if (v2ConnError[6] || v2ConnError[7]) {
              limbErrorByLandmark.add(L_HIP);
              limbErrorByLandmark.add(L_KNE);
              limbErrorByLandmark.add(L_ANK);
            }

            const allConnectionsOK = isPoseOKV2(poseFinalWorld, poseRef.current, flippedRef.current ? "left" : "right");
            frameOK = frameOK && allConnectionsOK;
            if (frameOK) {
              badFrames = 0;
              framesOK++;
            } else {
              badFrames++;
              framesOK = 0;
              // Tolerate a few bad frames so side-view face visibility jitter
              // does not immediately cancel an in-progress auto-capture.
              if (badFrames >= 4) {
                setOK(false);
              }
            }
            if (framesOK > 10) {
              setOK(true);
            }

            drawingUtils.drawLandmarks(
              poseFinalDraw.filter((lm, i) => visibleByIndex[i]),
              {
                radius: () => 15,
                color: (data) =>
                  data.index !== undefined &&
                  isOkLandmark(
                    poseFinalDraw as Landmark[],
                    data.index,
                    marginW,
                    marginH,
                    true
                  ) &&
                  !limbErrorByLandmark.has(data.index)
                    ? "green"
                    : "red",
              }
            );
            drawingUtils.drawConnectors(poseFinalDraw, connectionsToDraw, {
              lineWidth: 25,
              color: (data) => {
                if (data.index === undefined) {
                  return "gray";
                }
                const sourceIndex = connectionsToDraw[data.index]?.sourceIndex;
                return sourceIndex !== undefined && v2ConnError[sourceIndex]
                  ? "red"
                  : "green";
              },
            });
          }
        });
      }

      if (webcamRunning === true) {
        window.requestAnimationFrame(() =>
          renderLoop(id, poseLocalWorld, poseLocalDraw)
        );
      }
    }
    // Start render loop unconditionally — it idle-polls when shouldRunPose is false
    requestAnimationFrame(() => renderLoop(id));
    } catch (err) {
      console.error("MediaPipe: failed to load pose detector", err);
    }
  }
  return (
    <canvas
      id="canvas"
      style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", zIndex: 10, pointerEvents: "none" }}
    ></canvas>
  );
};
function calculateAveragePose(
  worldLandmarks: Landmark[],
  toShow: number[],
  poseLocalWorld: Landmark[][],
  videoSize?: { width: number; height: number }
) {
  const worldLandmarksToAdd = worldLandmarks
    .filter((data, i) => toShow.includes(i))
    .map((data) => {
      if (!MIRROR) {
        return data;
      } else {
        return {
          x: 1 - data.x,
          y: data.y,
          z: data.z,
          visibility: data.visibility,
        };
      }
    });

  if (!poseLocalWorld) {
    poseLocalWorld = [];
  } else if (poseLocalWorld.length > 15) {
    poseLocalWorld.shift();
  }
  poseLocalWorld.push(worldLandmarksToAdd);

  const poseFinalWorld = poseLocalWorld.reduce((acc, val) => {
    val.forEach((data, i) => {
      if (!acc[i]) {
        acc[i] = { x: 0, y: 0, z: 0, visibility: 0 };
      }
      acc[i].x += data.x;
      acc[i].y += data.y;
      acc[i].z += data.z;
      acc[i].visibility += data.visibility;
    });
    return acc;
  }, []);
  const len = poseLocalWorld.length;
  poseFinalWorld.forEach((data) => {
    data.x /= len;
    data.y /= len;
    data.z /= len;
    data.visibility /= len;
  });
  if (videoSize) {
    poseFinalWorld.forEach((data) => {
      data.x *= videoSize.width;
      data.y *= videoSize.height;
    });
  }
  return { result: poseFinalWorld, updatedList: poseLocalWorld };
}
